<template>
  <div class="weekly-workstatus-view">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="BriefcaseIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Work Status') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Weekly work status report') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center flex-wrap mt-1 mt-lg-0"
        style="gap: 8px;"
      >
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="isLoading"
          @click="applyFilters"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isLoading }"
          />
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('Export Sheet')"
          :disabled="isExporting"
          @click="openExportModal"
        >
          <b-spinner
            v-if="isExporting"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Export Sheet') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'telemetry-summary', params: { id: assetId } }"
        >
          <feather-icon
            icon="BarChart2Icon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Graphic Analysis') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'queries-month', params: { id: assetId } }"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Monthly Summary') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Compact Asset Info Header Card -->
    <b-card
      no-body
      class="asset-header-card mb-2 shadow-sm"
    >
      <div class="asset-header-content p-1 p-md-1">
        <!-- Skeleton Loading State -->
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center justify-content-between flex-wrap w-100 py-50"
        >
          <div class="d-flex align-items-center">
            <b-spinner
              variant="primary"
              small
              class="mr-1"
            />
            <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
          </div>
        </div>

        <!-- Loaded Content -->
        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <!-- Left: Back Button, Avatar, Plate, Marca, Modelo -->
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <!-- Back Button -->
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="$router.back()"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <!-- Vehicle Avatar -->
            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || $t('Model')"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <!-- Vehicle Identifiers: Plate + Marca / Modelo -->
            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <!-- Plate Badge -->
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted copy-icon"
                  />
                </div>

                <!-- Marca & Modelo -->
                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || assetId }}
                  </h5>
                </div>

                <!-- Nickname Tag if any -->
                <b-badge
                  v-if="assetData && assetData.nickname"
                  variant="light-primary"
                  class="ml-50 badge-glow font-weight-normal"
                >
                  {{ assetData.nickname }}
                </b-badge>
              </div>

              <!-- Subtitle with Chassis if no plate -->
              <div
                v-if="!assetPlate && assetChassis"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis, $t('chassis'))"
                  >
                    {{ assetChassis }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <!-- Right: Chassis Chip (when plate exists) & Action Button to Asset Summary -->
          <div class="d-flex align-items-center flex-wrap">
            <!-- Chassis Chip (only shown when plate is already shown on the left) -->
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer mr-1 mb-50 mb-md-0"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
              <feather-icon
                icon="CopyIcon"
                size="12"
                class="ml-50 text-muted copy-icon"
              />
            </div>

            <div
              v-if="assetGroupName"
              class="meta-chip mr-1 mb-50 mb-md-0"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <div
              v-if="assetCustomerName"
              class="meta-chip mr-1 mb-50 mb-md-0"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>

            <div class="asset-actions flex-shrink-0">
              <b-button
                v-b-tooltip.hover
                variant="outline-primary"
                size="sm"
                class="btn-view-details"
                :to="{ name: 'apps-asset-summary', params: { id: assetId } }"
                :title="$t('goToAssetSummary')"
              >
                <feather-icon
                  icon="ActivityIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('assetSummary') }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <transition name="slide-fade">
      <b-card
        v-if="showFilters"
        no-body
        class="mb-1 shadow-sm border-0 filters-card"
      >
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="text-primary mr-50"
            />
            <h6 class="mb-0 font-weight-bolder">
              {{ $t('Filters') }}
            </h6>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ $t('{count} active', { count: activeFiltersCount }) }}
            </b-badge>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              :title="$t('Clear Filters')"
              @click="resetFilters"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Close Filters')"
              @click="showFilters = false"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <div
            class="mb-1 d-flex flex-wrap align-items-center"
            style="gap: 6px;"
          >
            <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
            <b-badge
              v-for="preset in periodPresets"
              :key="preset.id"
              href="javascript:void(0)"
              :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset(preset.id)"
            >
              {{ preset.label }}
            </b-badge>
          </div>

          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="4"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Start Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="singleDateConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              lg="4"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('End Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="singleDateConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0 mr-1">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50 text-primary flex-shrink-0"
              />
              <span>
                {{ $t('Vehicle:') }}
                <strong class="text-primary">{{ assetModel || assetPlate || assetChassis || assetId }}</strong>.
                {{ $t('Choose an interval of up to 7 days') }}
              </span>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 8px;"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetFilters"
              >
                {{ $t('Restore Default') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                :disabled="isLoading || !dateStart || !dateEnd"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <div
      v-if="!showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
      <b-badge
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center"
      >
        <feather-icon
          icon="LockIcon"
          size="12"
          class="mr-25"
        />
        {{ assetModel || assetPlate || assetChassis || assetId }}
      </b-badge>
      <b-badge
        v-if="periodLabel"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="CalendarIcon"
          size="12"
          class="mr-25"
        />
        {{ periodLabel }}
      </b-badge>
      <b-link
        class="font-small-2 font-weight-bold ml-50 text-danger"
        @click="resetFilters"
      >
        {{ $t('Clear') }}
      </b-link>
    </div>

    <b-card class="shadow-sm border-0">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
        <p class="font-weight-bolder mb-50 mb-md-0">
          {{ $t('Legend') }}
        </p>
        <div class="d-flex flex-wrap align-items-center legend-row">
          <div class="status">
            <span class="dot dot-off" />
            <span class="font-weight-bolder">{{ $t('off') }}</span>
          </div>
          <div class="status">
            <span class="dot dot-on" />
            <span class="font-weight-bolder">{{ $t('on') }}</span>
          </div>
          <div class="status">
            <span class="dot dot-no-connection" />
            <span class="font-weight-bolder">{{ $t('No Connection') }}</span>
          </div>
          <div class="status">
            <span class="dot dot-idle" />
            <span class="font-weight-bolder">{{ $t('iddle') }}</span>
          </div>
          <div class="status">
            <span class="dot dot-severe-use" />
            <span class="font-weight-bolder">{{ $t('severe') }}</span>
          </div>
          <div class="status">
            <span class="dot dot-failure" />
            <span class="font-weight-bolder">{{ $t('failure') }}</span>
          </div>
        </div>
      </div>
      <b-row class="mt-1">
        <b-col md="12">

          <div
            v-if="!isLoading"
            id="chart"
          >
            <vue-apex-charts
              ref="chart"
              type="rangeBar"
              height="300"
              :options="chartOptions"
              :series="series"
            />
          </div>
          <div
            v-if="isLoading"
            class="text-center py-3"
          >
            <b-spinner
              variant="primary"
              class="mb-1"
            />
            <div class="text-muted font-small-3">
              {{ $t('Loading') }}
            </div>
          </div>
        </b-col>
      </b-row>

      <b-modal
        v-model="showExportModal"
        :title="$t('Export weekly work status sheet')"
        centered
        content-class="shadow"
        hide-footer
        size="md"
        @show="onExportModalShow"
      >
        <p class="text-muted mb-2 small export-modal-hint">
          {{ $t('Select period for export description') }}
        </p>
        <b-form-group
          :label="$t('Choose the Date')"
          label-for="export-weekly-date-range"
          class="mb-0"
        >
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text">
                <feather-icon icon="CalendarIcon" />
              </span>
            </div>
            <flat-pickr
              id="export-weekly-date-range"
              v-model="exportSelectedDates"
              class="form-control"
              :config="exportPickerConfig"
              @on-change="onExportFlatpickrChange"
            />
          </div>
        </b-form-group>
        <div class="d-flex justify-content-end flex-wrap mt-2 pt-2 border-top">
          <b-button
            variant="outline-secondary"
            class="mr-50 mb-50 mb-sm-0"
            @click="showExportModal = false"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="isExporting || !isExportRangeComplete"
            @click="confirmExport"
          >
            <b-spinner
              v-if="isExporting"
              class="mr-50"
              small
            />
            {{ $t('Confirm') }}
          </b-button>
        </div>
      </b-modal>

      <!-- Modal: Primeira visita (informativo exportação) -->
      <b-modal
        id="weekly-workstatus-first-visit-modal"
        size="md"
        centered
        modal-class="weekly-workstatus-first-visit-modal"
        no-close-on-backdrop
        hide-header-close
        @hidden="onWeeklyWorkStatusFirstVisitModalHidden"
      >
        <template #modal-header>
          <div class="d-flex align-items-center w-100">
            <div class="wws-announcement-icon-wrapper mr-3">
              <feather-icon
                icon="FileTextIcon"
                size="26"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0 font-weight-bolder">
                {{ $t('Weekly Work Status First Visit Modal Title') }}
              </h5>
              <small class="text-muted">{{ $t('Weekly Work Status First Visit Modal Subtitle') }}</small>
            </div>
          </div>
        </template>
        <div class="wws-announcement-content">
          <p class="mb-3 text-muted small">
            {{ $t('Weekly Work Status First Visit Modal Intro') }}
          </p>
          <ul class="wws-announcement-list mb-0">
            <li class="mb-2">
              <strong>{{ $t('Weekly Work Status First Visit Modal Range Title') }}</strong>
              <span>{{ $t('Weekly Work Status First Visit Modal Range Text') }}</span>
            </li>
            <li class="mb-0">
              <strong>{{ $t('Weekly Work Status First Visit Modal Fields Title') }}</strong>
              <span>{{ $t('Weekly Work Status First Visit Modal Fields Text') }}</span>
            </li>
          </ul>
        </div>
        <template #modal-footer>
          <b-button
            variant="primary"
            @click="$bvModal.hide('weekly-workstatus-first-visit-modal')"
          >
            <feather-icon
              icon="CheckIcon"
              size="16"
              class="mr-1"
            />
            {{ $t('Got it') }}
          </b-button>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle, import/no-cycle, no-unused-expressions, consistent-return, no-param-reassign, vue/no-unused-components, import/no-extraneous-dependencies, no-unused-vars, implicit-arrow-linebreak */
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BModal,
  BFormGroup,
  BBadge,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'

import router from '@/router'
import store from '@/store'

import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt'

import VueApexCharts from 'vue-apexcharts'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { resolveIcon } from '@/utils/assetUtils'
import defaultCarIcon from '@/assets/images/assets/Car.png'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'

import queriesStoreModule from '../queriesStoreModule'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BModal,
    BFormGroup,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    flatPickr,
    VueApexCharts,
  },
  data() {
    return {
      // Asset Info
      assetData: null,
      loadingAsset: false,

      isExporting: false,
      isLoading: false,
      showFilters: false,
      dateStart: '',
      dateEnd: '',
      dateRangePreset: 'last7',
      ignoreDateChange: false,
      singleDateConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y',
        altInputClass: 'form-control form-control-sm',
        maxDate: new Date(),
        locale: getFlatpickrLocale(),
      },
      WEEKLY_WORKSTATUS_FIRST_VISIT_MODAL_KEY: 'weekly-workstatus-first-visit-modal-v1',
      showExportModal: false,
      exportSelectedDates: '',
      exportPickerConfig: {
        mode: 'range',
        maxDate: new Date(),
        // Portuguese usa rangeSeparator " até "; fixamos " to " para alinhar ao restante do componente e às validações
        locale: { ...Portuguese, rangeSeparator: ' to ' },
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y',
      },
      selectedDates: '',
      pickerConfig: {
        mode: 'range',
        maxDate: new Date(),
      },

      datesInRange: [],

      series: [{ data: [] }],
      chartOptions: {
        chart: {
          height: 300,
          type: 'rangeBar',
        },

        legend: {
          show: false,
          // position: 'bottom'
        },

        plotOptions: {
          bar: {
            horizontal: true,
            // barHeight: '50%',
            // rangeBarGroupRows: true
          },
        },

        xaxis: {
          type: 'datetime',
          labels: {
            // datetimeUTC: false,
            format: 'HH:mm',
          },
        },
        yaxis: {
          type: 'category',
        },
        tooltip: {
          custom(data) {
            const hourStart = String(new Date(data.y1).getUTCHours()).padStart(2, '0')

            const minuteStart = String(new Date(data.y1).getUTCMinutes()).padStart(2, '0')

            const hourEnd = String(new Date(data.y2).getUTCHours()).padStart(2, '0')
            const minuteEnd = String(new Date(data.y2).getUTCMinutes()).padStart(2, '0')
            // const values = data.ctx.rangeBar.getTooltipValues(data);
            return `${'<div class="px-1 py-50"><span>'}${
              `${hourStart}:${minuteStart} to ${hourEnd}:${minuteEnd}`
            }</span></div>`
          },
        },
      },
    }
  },
  computed: {
    isExportRangeComplete() {
      return this.parseExportRangeParts(this.exportSelectedDates) !== null
    },
    assetId() {
      return this.myId || router.currentRoute.params.id || ''
    },
    assetPlate() {
      return this.assetData?.plate || ''
    },
    assetChassis() {
      return this.assetData?.chassisNumber || ''
    },
    assetManufacturer() {
      return this.assetData?.manufacturerName || this.assetData?.brandName || this.assetData?.manufacturer || ''
    },
    assetModel() {
      if (!this.assetData) return ''
      const name = this.assetData.modelIdName || this.assetData.modelName || ''
      const year = this.assetData.modelYear ? `(${this.assetData.modelYear})` : ''
      return `${name} ${year}`.trim()
    },
    assetAvatar() {
      if (!this.assetData) return resolveIcon(null, null)
      return resolveIcon(this.assetData.assetType, this.assetData.iconUrl)
    },
    assetGroupName() {
      return this.assetData?.assetGroupName || this.assetData?.groupName || ''
    },
    assetCustomerName() {
      return this.assetData?.customerName || this.assetData?.ownerName || ''
    },
    activeFiltersCount() {
      return this.dateStart && this.dateEnd ? 1 : 0
    },
    periodLabel() {
      if (!this.dateStart && !this.dateEnd) return ''
      return `${this.formatYmd(this.dateStart)} — ${this.formatYmd(this.dateEnd)}`
    },
    periodPresets() {
      return [
        { id: 'today', label: this.$t('Today') },
        { id: 'yesterday', label: this.$t('Yesterday') },
        { id: 'last7', label: this.$t('Last 7 days') },
      ]
    },
  },
  watch: {},
  mounted() {
    this.loadAssetData()
    this.getStartDate()
    this.generateReport()

    // Exibir modal informativo apenas na primeira visita
    this.$nextTick(() => {
      try {
        if (
          typeof localStorage !== 'undefined'
          && !localStorage.getItem(this.WEEKLY_WORKSTATUS_FIRST_VISIT_MODAL_KEY)
        ) {
          if (this.$bvModal && typeof this.$bvModal.show === 'function') {
            this.$bvModal.show('weekly-workstatus-first-visit-modal')
          }
        }
      } catch (e) {
        // localStorage pode estar indisponível
      }
    })
  },
  methods: {
    async loadAssetData() {
      if (!this.assetId) return
      this.loadingAsset = true
      try {
        const response = await store.dispatch('app-queries/fetchAsset', { id: this.assetId })
        if (response && response.data) {
          this.assetData = response.data
        }
      } catch (error) {
        // Silently fail
      } finally {
        this.loadingAsset = false
      }
    },

    handleImageError(event) {
      event.target.src = defaultCarIcon
    },

    copyToClipboard(text, label) {
      if (!text) return
      this.$copyText(text).then(() => {
        this.showSnackbar({
          title: `${label || this.$t('Text')} ${this.$t('copied to clipboard')}!`,
          icon: 'CheckIcon',
          variant: 'success',
        })
      }).catch(() => {
        this.showSnackbar({
          title: this.$t('Failed to copy to clipboard'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
      })
    },

    onWeeklyWorkStatusFirstVisitModalHidden() {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(this.WEEKLY_WORKSTATUS_FIRST_VISIT_MODAL_KEY, 'true')
        }
      } catch (e) {
        // localStorage pode estar indisponível
      }
    },

    /**
     * Flatpickr em PT usa " até " como separador; valor inicial usa " to ". Aceita ambos.
     * @returns {[string, string]|null} datas Y-m-d
     */
    parseExportRangeParts(str) {
      if (!str || typeof str !== 'string') return null
      const trimmed = str.trim()
      const parts = trimmed.split(/\s+(?:to|até)\s+/i)
      if (parts.length !== 2) return null
      const ymd = /^\d{4}-\d{2}-\d{2}$/
      const a = parts[0].trim()
      const b = parts[1].trim()
      if (!ymd.test(a) || !ymd.test(b)) return null
      return [a, b]
    },

    inclusiveDayCountYmd(startStr, endStr) {
      const start = new Date(`${startStr}T12:00:00`)
      const end = new Date(`${endStr}T12:00:00`)
      const diff = Math.round((end - start) / (24 * 60 * 60 * 1000))
      return diff + 1
    },

    toYmd(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    getDefaultSevenDayRangeString() {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - 6)
      return `${this.toYmd(start)} to ${this.toYmd(today)}`
    },

    onExportModalShow() {
      const fromChart = this.getDates()
      if (
        fromChart
        && fromChart.length === 2
        && this.inclusiveDayCountYmd(fromChart[0], fromChart[1]) <= 31
      ) {
        this.exportSelectedDates = `${fromChart[0]} to ${fromChart[1]}`
      } else {
        this.exportSelectedDates = this.getDefaultSevenDayRangeString()
      }
    },

    openExportModal() {
      this.showExportModal = true
    },

    onExportFlatpickrChange() {
      this.$nextTick(() => {
        const dateStr = this.exportSelectedDates
        if (dateStr && this.parseExportRangeParts(dateStr)) {
          this.ensureExportMaxRangeYmd(dateStr)
        }
      })
    },

    ensureExportMaxRangeYmd(dateStr) {
      const parts = this.parseExportRangeParts(dateStr)
      if (!parts) return
      const [startStr, endStr] = parts
      const start = new Date(`${startStr}T12:00:00`)
      const end = new Date(`${endStr}T12:00:00`)
      const maxStart = new Date(end)
      maxStart.setDate(maxStart.getDate() - 30)
      if (start < maxStart) {
        this.showSnackbar({
          title: this.$t('Maximum interval of 31 days.'),
          icon: 'InfoIcon',
          variant: 'info',
        })
        this.exportSelectedDates = `${this.toYmd(maxStart)} to ${endStr}`
      }
    },

    confirmExport() {
      const rangeParts = this.parseExportRangeParts(this.exportSelectedDates)
      if (!rangeParts) {
        this.showSnackbar({
          title: this.$t('Select a complete date range'),
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        })
        return
      }
      const [startStr, endStr] = rangeParts
      if (this.inclusiveDayCountYmd(startStr, endStr) > 31) {
        this.showSnackbar({
          title: this.$t('Range not accepted. The interval must be 31 days or less.'),
          icon: 'AlertTriangleIcon',
          variant: 'info',
        })
        return
      }
      this.exportToExcel(startStr, endStr)
    },

    showSnackbar(config = { title: '', icon: '', variant: 'info' }) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: config.title,
          icon: config.icon,
          variant: config.variant,
        },
      })
    },

    async exportToExcel(startDate, endDate) {
      this.isExporting = true

      await store.dispatch('app-queries/exportWeeklyWorksatusToExcel', {
        startDate,
        endDate,
        assetId: router.currentRoute.params.id,
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${this.$t('workstatus')} (${new Date()}).xls`)
        document.body.appendChild(link)
        link.click()
        this.isExporting = false
        this.showExportModal = false
      }).catch(err => {
        this.isExporting = false
        this.showSnackbar({
          title: this.$t('Error exporting report'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
      })
    },

    getDates() {
      if (this.selectedDates.includes('to')) {
        const datesArray = this.selectedDates.split(' to ')
        const startDate = new Date(datesArray[0])
        const endDate = new Date(datesArray[1])
        // return [startDate, endDate]
        return datesArray
      }
    },

    resolveColor(status) {
      if (!status) return '#fff'
      if (status === 'failure') return '#ea5455'
      if (status === 'off') return '#82868b'
      if (status === 'noconnection') return '#f3f2f7'
      if (status === 'severe') return '#ff9f43'
      if (status === 'iddle') return '#00cfe8'
      if (status === 'on') return '#28c76f'
      return '#28c76f'
    },

    getStartDate() {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - 6)
      this.dateStart = this.toYmd(start)
      this.dateEnd = this.toYmd(today)
      this.dateRangePreset = 'last7'
      this.selectedDates = `${this.dateStart} to ${this.dateEnd}`
    },

    formatYmd(value) {
      if (!value) return ''
      const [year, month, day] = String(value).split('-')
      if (!year || !month || !day) return String(value)
      return `${day}/${month}/${year}`
    },

    setDatePreset(preset) {
      this.ignoreDateChange = true
      const today = new Date()
      if (preset === 'today') {
        const day = this.toYmd(today)
        this.dateStart = day
        this.dateEnd = day
      } else if (preset === 'yesterday') {
        const day = new Date(today)
        day.setDate(day.getDate() - 1)
        const formatted = this.toYmd(day)
        this.dateStart = formatted
        this.dateEnd = formatted
      } else {
        const start = new Date(today)
        start.setDate(today.getDate() - 6)
        this.dateStart = this.toYmd(start)
        this.dateEnd = this.toYmd(today)
      }
      this.dateRangePreset = preset
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
    },

    onCustomDateChange() {
      if (this.ignoreDateChange) return
      this.dateRangePreset = ''
    },

    applyFilters() {
      if (!this.dateStart || !this.dateEnd) {
        this.showFilters = true
        return
      }
      if (this.dateStart > this.dateEnd) {
        const swap = this.dateStart
        this.dateStart = this.dateEnd
        this.dateEnd = swap
      }
      this.selectedDates = `${this.dateStart} to ${this.dateEnd}`
      this.generateReport()
    },

    resetFilters() {
      this.getStartDate()
      this.generateReport()
    },

    getMonthAndDay(dt) {
      const date = new Date(dt)

      const day = date.getDate()
      const month = date.getMonth() + 1

      const formattedDay = String(day).padStart(2, '0')
      const formattedMonth = String(month).padStart(2, '0')

      const formattedDate = `${formattedDay}/${formattedMonth}`

      return formattedDate
    },

    fetchReport(date) {
      // this.isLoading = true
      if (date.includes('Z')) {
        date = date.slice(0, -1)
      }
      return store
        .dispatch('app-queries/fetchAssetWorkstatusHistory', {
          id: router.currentRoute.params.id,
          dt: date,

        })
        .then(response =>

        // dataWorkStatus.value = [];

          response.data.data)
        .catch(e => {
          this.isLoading = false
          this.showSnackbar({
            title: this.$t('Error fetching report'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          })
        })
    },

    utcToLocalDate(date) {
      const myDate = new Date(2023, 9, 7, date.getHours(), date.getMinutes())
      // var myDate = date

      const offset = myDate.getTimezoneOffset() * 60 * 1000

      const withOffset = myDate.getTime()
      const withoutOffset = withOffset - offset

      return myDate.getTime()

      // return withoutOffset;
    },

    // utcToLocalDate(utcDate) {

    //     // Defina o deslocamento horário de Brasília em minutos (-3 horas)
    //     const offsetBrasilia = -3 * 60;

    //     // Crie um novo objeto de data com o deslocamento aplicado
    //     const localDate = new Date(utcDate.getTime() + offsetBrasilia * 60 * 1000);

    //     localDate.setFullYear(2023)
    //     localDate.setMonth(7)
    //     localDate.setDate(9)

    //     return localDate.getTime();
    // },

    generateReport() {
      this.series[0].data = []
      // this.series = []
      if (this.selectedDates.includes('to')) {
        const datesArray = this.selectedDates.split(' to ')
        const startDateString = datesArray[0]
        const endDateString = datesArray[1]
        const startDate = new Date(`${startDateString}T00:00:00`)
        const endDate = new Date(`${endDateString}T23:59:59`)

        this.getDatesInRange(startDate, endDate)
      }
    },
    parseDateWithoutTimezone(dateString) {
      const [datePart, timePart] = dateString.split('T')
      const [year, month, day] = datePart.split('-').map(Number)
      const [hour, minute, second] = timePart.split(':').map(Number)

      const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
      return date.getTime()
    },

    async getDatesInRange(startDate, endDate) {
      this.datesInRange = []
      const oneDay = 24 * 60 * 60 * 1000

      const start = new Date(startDate)
      const end = new Date(endDate)

      if ((end - start) / oneDay > 7) {
        this.showSnackbar({
          title: this.$t('Range not accepted. The interval must be 7 dias or less.'),
          icon: 'AlertTriangleIcon',
          variant: 'info',
        })
        return
      }

      for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        this.datesInRange.push(new Date(date))
      }

      if (this.datesInRange.length > 0) {
        this.isLoading = true

        // Garante a ordem decrescente (do dia mais recente para o mais antigo)
        this.datesInRange.sort((a, b) => b - a)

        // --- ETAPA 1: Definir a ordem correta das categorias (dias) no eixo Y ---
        // Isso resolve o problema da inversão dos dias 16 e 17.
        const yAxisCategories = this.datesInRange.map(date => this.getMonthAndDay(date))
        this.chartOptions = {
          ...this.chartOptions, // Mantém as outras opções
          yaxis: {
            categories: yAxisCategories,
          },
        }

        // --- ETAPA 2: Buscar e processar TODOS os dados antes de enviá-los para o gráfico ---
        const isoDates = this.datesInRange.map(date => date.toISOString())
        const chartDataPromises = isoDates.map(isoDate => this.fetchReport(isoDate))
        const chartDataArrays = await Promise.all(chartDataPromises)

        // Usamos um Map para agrupar os intervalos por dia. A chave é o dia (ex: "17/09")
        // e o valor é um array com os pedaços de dados daquele dia.
        const dataByDay = new Map()

        // Pré-populamos o Map com as chaves dos dias na ordem correta, para garantir a organização.
        yAxisCategories.forEach(day => {
          dataByDay.set(day, [])
        })

        // Achatamos o array de arrays para ter uma lista única de todos os intervalos retornados pela API.
        const allIntervals = chartDataArrays.flat()

        allIntervals.forEach(dataItem => {
          const itemStartDate = new Date(dataItem.start)
          const itemEndDate = new Date(dataItem.end)

          // A lógica de divisão de intervalos que cruzam a meia-noite continua a mesma
          if (itemStartDate.getDate() !== itemEndDate.getDate()) {
            // Parte 1: Final do primeiro dia
            const day1Label = this.getMonthAndDay(itemStartDate)
            if (dataByDay.has(day1Label)) {
              dataByDay.get(day1Label).push({
                x: day1Label,
                y: [
                  new Date(`2025-04-10T${this.dateStringToTimespan(dataItem.start)}:00Z`).getTime(),
                  new Date('2025-04-10T23:59:59Z').getTime(),
                ],
                fillColor: this.resolveColor(dataItem.workStatus),
              })
            }

            // Parte 2: Início do segundo dia
            const day2Label = this.getMonthAndDay(itemEndDate)
            if (dataByDay.has(day2Label)) {
              dataByDay.get(day2Label).push({
                x: day2Label,
                y: [
                  new Date('2025-04-10T00:00:00Z').getTime(),
                  new Date(`2025-04-10T${this.dateStringToTimespan(dataItem.end)}:00Z`).getTime(),
                ],
                fillColor: this.resolveColor(dataItem.workStatus),
              })
            }
          } else {
            // Intervalo que não cruza a meia-noite
            const dayLabel = this.getMonthAndDay(itemEndDate)
            if (dataByDay.has(dayLabel)) {
              dataByDay.get(dayLabel).push({
                x: dayLabel,
                y: [
                  new Date(`2025-04-10T${this.dateStringToTimespan(dataItem.start)}:00Z`).getTime(),
                  new Date(`2025-04-10T${this.dateStringToTimespan(dataItem.end)}:00Z`).getTime(),
                ],
                fillColor: this.resolveColor(dataItem.workStatus),
              })
            }
          }
        })

        // --- ETAPA 3: Ordenar os dados de cada dia e montar a série final ---
        // Esta é a etapa crucial que resolve a sobreposição.
        let finalChartData = []
        dataByDay.forEach((dayIntervals, day) => {
          // Ordena os intervalos de cada dia pela sua data de início.
          const sortedIntervals = dayIntervals.sort((a, b) => a.y[0] - b.y[0])
          finalChartData = finalChartData.concat(sortedIntervals)
        })

        this.series = [{
          data: finalChartData,
        }]

        this.isLoading = false
      }
    },

    dateStringToTimespan(dataHoraString) {
      return dataHoraString.substring(11, 16)
    },

    msgDialog(msg, icon) {
      this.$swal({
        position: 'top-end',
        icon,
        title: icon,
        text: msg,
        showConfirmButton: false,
        timer: 1500 * 3,
        customClass: {
          confirmButton: 'btn btn-primary',
        },

        buttonsStyling: false,
      })
    },

  },
  setup() {
    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'
    // Register module
    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.unregisterModule(QUERIES_APP_STORE_MODULE_NAME)
    })

    const myId = ref(router.currentRoute.params.id)

    return {
      QUERIES_APP_STORE_MODULE_NAME,
      myId,

    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.weekly-workstatus-view {
  .spin-icon {
    animation: weekly-workstatus-spin 1s linear infinite;
  }

  .filters-card {
    border-radius: 0.5rem;
  }

  .legend-row {
    gap: 12px;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.18s ease;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@keyframes weekly-workstatus-spin {
  100% {
    transform: rotate(360deg);
  }
}

/* Modal primeira visita - sem scoped para atingir conteúdo renderizado no body */
.weekly-workstatus-first-visit-modal {
  .wws-announcement-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(115, 103, 240, 0.12);
    flex-shrink: 0;
  }

  .wws-announcement-list {
    padding-left: 1.15rem;

    strong {
      margin-right: 0.35rem;
    }
  }
}
</style>
<style lang="scss">
.status {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 10px;
}

.dot {
  height: 15px;
  width: 15px;
  border-radius: 50%;
  display: inline-block;
  margin-bottom: 5px;
}

.dot-off {
  background-color: #747679;
}

.dot-on {
  background-color: #47cd83;
}

.dot-no-connection {
  background-color: rgba(198, 217, 245, 0.85);
}

.dot-idle {
  background-color: #25d4ea;
}

.dot-severe-use {
  background-color: #feab5e;
}

.dot-failure {
  background-color: #ea5455;
}

// Asset Header Card Styling
.asset-header-card {
  border: 1px solid rgba(115, 103, 240, 0.14) !important;
  border-left: 4px solid #7367f0 !important;
  border-radius: 0.5rem;
  background-color: #ffffff;
  transition: all 0.2s ease-in-out;

  .asset-header-content {
    min-height: 60px;
    display: flex;
    align-items: center;
  }

  .back-btn {
    border: 1px solid rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
    &:hover {
      background-color: rgba(115, 103, 240, 0.1) !important;
      color: #7367f0 !important;
      border-color: #7367f0;
    }
  }

  .asset-avatar-box {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

    .asset-avatar-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 4px;
      transition: transform 0.2s ease;
    }

    &:hover .asset-avatar-img {
      transform: scale(1.08);
    }
  }

  .license-plate-badge {
    display: inline-flex;
    align-items: center;
    background: #ffffff;
    border: 2px solid #2b2b2b;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 1px;
    color: #2b2b2b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.15s ease;

    .plate-number {
      font-family: 'Consolas', 'Courier New', monospace;
    }

    .copy-icon {
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }

    &:hover {
      border-color: #7367f0;
      color: #7367f0;
      background-color: rgba(115, 103, 240, 0.05);

      .copy-icon {
        opacity: 1;
        color: #7367f0 !important;
      }
    }
  }

  .asset-brand-text {
    font-size: 1.05rem;
    letter-spacing: -0.2px;
    color: #7367f0 !important;
  }

  .asset-model-title {
    font-size: 1.05rem;
    letter-spacing: -0.2px;
  }

  .meta-chip {
    display: inline-flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #ebedf0;
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 0.8rem;
    transition: all 0.15s ease;
    cursor: default;

    &.cursor-pointer, &[class*="cursor-pointer"] {
      cursor: pointer;
      &:hover {
        background: #f0f2f5;
        border-color: #7367f0;
      }
    }

    .meta-label {
      font-weight: 600;
      color: #6e6b7b;
      margin-right: 4px;
    }

    .meta-value {
      font-weight: 500;
      color: #5e5873;
    }

    .copy-icon {
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }

    &:hover .copy-icon {
      opacity: 1;
      color: #7367f0 !important;
    }
  }

  .btn-view-details {
    font-weight: 600;
    border-radius: 6px;
    padding: 0.45rem 0.85rem;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
  }
}

// Dark layout support
body.dark-layout,
.dark-layout {
  .asset-header-card {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    border-left-color: #7367f0 !important;

    .back-btn {
      border-color: rgba(255, 255, 255, 0.12);
      color: #b4b7bd;
    }

    .asset-avatar-box {
      background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
      border-color: rgba(255, 255, 255, 0.1);
    }

    .license-plate-badge {
      background: #161d31;
      border-color: #4b4b5a;
      color: #d0d2d6;

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
        background-color: rgba(115, 103, 240, 0.1);
      }
    }

    .asset-brand-text {
      color: #9086f4 !important;
    }

    .asset-model-title {
      color: #d0d2d6 !important;
    }

    .meta-chip {
      background: #161d31;
      border-color: rgba(255, 255, 255, 0.08);

      &.cursor-pointer:hover {
        background: #1e2433;
        border-color: #7367f0;
      }

      .meta-label {
        color: #b4b7bd;
      }

      .meta-value {
        color: #d0d2d6;
      }
    }
  }
}
</style>
